import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { Button, Card, Form, Input, Modal, Select, Space, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Grid as ZhGrid, GridItem as ZhGridItem, Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import request from "@/api";
import { DEFAULT_VALUE } from "@/config";
import Permission from "@/views/components/Permission";
import InfoPair from "@/views/components/InfoPair";
import { TableSearchIcon } from "@/views/components/TableSearchIcon";
import { useUserStore } from "@/views/stores";
import { showRequestError } from "@/utils/requestErrorMessage";

export interface MemberRechargeCustomer {
    name?: string;
    realName?: string;
    institutionName?: string;
    mobile?: string;
    code?: string;
    id?: number;
}

export interface MemberRechargeRow {
    id: number | string;
    customerInfo?: MemberRechargeCustomer;
    memberLevel?: number | string;
    memberDay?: number;
    openMode?: number | string;
    activationCode?: string;
    amount?: number;
    activationDate?: string;
    expireDate?: string;
    createdTime?: string;
}

export interface MemberRechargeQuery {
    page: number;
    size: number;
    id?: string;
    customerKeyWord?: string;
    memberLevel?: string;
    openMode?: string;
    activationCode?: string;
    sortKey?: string;
    sortOrder?: string;
}

export interface ActivationCodeInfo {
    level?: number | string;
    duration?: number;
    state?: number | string;
    hasBind?: number | boolean;
    customerInfo?: MemberRechargeCustomer;
}

export interface MemberRechargePageProps {
    loadRecords?: (query: MemberRechargeQuery) => Promise<{ list?: MemberRechargeRow[]; total?: number }>;
    lookupCode?: (code: string) => Promise<ActivationCodeInfo | undefined>;
    searchCustomers?: (searchParam: string) => Promise<MemberRechargeCustomerOption[]>;
    exchange?: (params: { code: string; customerId?: number }) => Promise<unknown>;
}

export interface MemberRechargeCustomerOption {
    id: number;
    label: string;
    /** 保留旧 Vue 选项中的完整账号对象，用于兑换确认提示。 */
    customer?: MemberRechargeCustomer;
}

const defaultQuery: MemberRechargeQuery = { page: 1, size: 10 };
const memberLevelLabels: Record<number, string> = { 2: "基础会员", 3: "高级会员" };
const openModeLabels: Record<number, string> = { 1: "微信支付", 2: "支付宝", 3: "激活码", 4: "APPLE PAY" };
/** 按 visual-diff 对齐 Vue 旧版兑换弹窗的表单与底部操作区留白。 */
const exchangeDialogModalStyles = {
    // Antd Modal body 自带 24px 横向内容区，补 7px 后与 Vue 20px body padding 的字段起点一致。
    body: { padding: "19px 7px 26px" },
    // body 总高度与旧版保持一致；footer 扩展 4px 抵消 Antd 内容区的横向留白。
    footer: { marginInline: -4, paddingTop: 1 }
};
/** Vue large 按钮横向内边距为 20px，兑换表单按钮宽度需保持 68px。 */
const exchangeDialogButtonProps = { style: { paddingInline: 19 } };
/** 对齐 Vue ElMessageBox.confirm 的标题、提示和底部按钮垂直留白。 */
const exchangeConfirmModalStyles = {
    container: { borderRadius: 4, padding: "20px 0" },
    header: { borderBottom: "1px solid #d9d9d9", marginBottom: 0, padding: "0 20px 20px" },
    title: { color: "#000000e0", fontSize: 16, fontWeight: 500, lineHeight: "16px" },
    // 兑换确认文案在 420px 宽度下保持两行，与 Vue message-box 的 97px 内容区一致。
    body: { height: 97, lineHeight: "24px", padding: "19px 20px 30px" },
    footer: { marginTop: 0, minHeight: 42, padding: "10px 20px 0" }
};
/** Vue Element Plus 的 large 按钮横向内边距为 20px，Antd 默认值需要补齐。 */
const exchangeConfirmButtonProps = { style: { paddingInline: 19 } };

/** 兼容 Vue 接口的数字/布尔 hasBind，并为旧测试数据保留 customerInfo 兜底。 */
const isActivationCodeBound = (info: ActivationCodeInfo) =>
    info.hasBind === undefined ? Boolean(info.customerInfo?.id) : info.hasBind === true || Number(info.hasBind) === 1;

/** 会员充值记录沿用 Vue 的会员等级枚举，过滤掉非会员空值。 */
const formatMemberLevel = (value?: number | string) => memberLevelLabels[Number(value)] || DEFAULT_VALUE;

/** 会员账号沿用 Vue 的 name/realName/mobile/code 组合显示方式。 */
const formatCustomer = (customer?: MemberRechargeCustomer) => {
    if (!customer) return DEFAULT_VALUE;
    return (
        [customer.name, customer.realName, customer.mobile, customer.code].filter(Boolean).join("|") || DEFAULT_VALUE
    );
};

/** 复用旧 Vue 兑换确认框的账号展示顺序和分隔符。 */
const formatExchangeCustomer = (customer?: MemberRechargeCustomer, optionLabel?: string) => {
    if (optionLabel) return optionLabel;
    if (!customer) return DEFAULT_VALUE;
    return ` ${customer.institutionName || customer.realName || DEFAULT_VALUE} | ${customer.mobile ?? ""} | ${
        customer.code || DEFAULT_VALUE
    }`;
};

const formatDateOnly = (value?: string) => (value ? value.slice(0, 10) : DEFAULT_VALUE);
const formatDateTime = (value?: string) => (value ? value.replace("T", " ").slice(0, 16) : DEFAULT_VALUE);

/** 金额保留 Vue 页面展示的两位小数。 */
const formatAmount = (value?: number) => (value == null ? DEFAULT_VALUE : Number(value).toFixed(2));

/** 复用 Vue `/admin/member-open-record/paging` 会员充值分页接口。 */
export async function loadMemberRechargeRecords(
    query: MemberRechargeQuery
): Promise<{ list?: MemberRechargeRow[]; total?: number }> {
    const response = await request.service.get("/admin/member-open-record/paging", { params: query });
    return response.data;
}

/** 复用 Vue 激活码详情接口，供兑换弹窗校验激活码状态。 */
export async function lookupMemberActivationCode(code: string): Promise<ActivationCodeInfo | undefined> {
    const response = await request.service.get(`/admin/activation/code/find/${code}`);
    return response.data;
}

/** 复用 Vue 未绑定激活码的关联账号搜索参数 `{ searchParam }`。 */
export async function searchMemberRechargeCustomers(searchParam: string): Promise<MemberRechargeCustomerOption[]> {
    const response = await request.service.get<Array<{ customer: MemberRechargeCustomer }>>(
        "/admin/customer/shipping/selector",
        { params: { searchParam } }
    );
    return response.data.map(item => ({
        id: item.customer.id as number,
        label: formatCustomer(item.customer),
        customer: item.customer
    }));
}

/** 复用 Vue 激活码兑换接口。 */
export async function exchangeMemberActivationCode(params: { code: string; customerId?: number }): Promise<unknown> {
    const response = await request.service.get("/admin/activation/code/exchange", { params });
    return response.data;
}

function ExchangeDialog({
    open,
    lookupCode,
    searchCustomers,
    exchange,
    onClose,
    onUpdated
}: {
    open: boolean;
    lookupCode: (code: string) => Promise<ActivationCodeInfo | undefined>;
    searchCustomers: (searchParam: string) => Promise<MemberRechargeCustomerOption[]>;
    exchange: (params: { code: string; customerId?: number }) => Promise<unknown>;
    onClose: () => void;
    onUpdated: () => void;
}) {
    const banButton = useUserStore(state => state.banButton);
    const [code, setCode] = useState("");
    const [codeInfo, setCodeInfo] = useState<ActivationCodeInfo>();
    const [customerOptions, setCustomerOptions] = useState<MemberRechargeCustomerOption[]>([]);
    const [customerId, setCustomerId] = useState<number>();
    const [customerKeyword, setCustomerKeyword] = useState("");
    const [customerSearchError, setCustomerSearchError] = useState("");
    // 字段校验错误必须挂在对应 Form.Item 下方，避免被 PageAlert 转成全局消息。
    const [codeError, setCodeError] = useState("");
    const [customerError, setCustomerError] = useState("");
    const [loading, setLoading] = useState(false);
    const [confirmData, setConfirmData] = useState<{ code: string; customerId: number; text: string }>();
    const [confirmLoading, setConfirmLoading] = useState(false);
    const customerSearchRequestRef = useRef(0);
    const customerSearchKeywordRef = useRef("");

    useEffect(() => {
        if (!open) return;
        setCode("");
        setCodeInfo(undefined);
        setCustomerOptions([]);
        setCustomerId(undefined);
        setCustomerKeyword("");
        customerSearchKeywordRef.current = "";
        customerSearchRequestRef.current += 1;
        setCustomerSearchError("");
        setCodeError("");
        setCustomerError("");
        setConfirmData(undefined);
        setConfirmLoading(false);
    }, [open]);

    const handleCodeChange = async (value: string) => {
        setCode(value);
        setCodeInfo(undefined);
        setCustomerOptions([]);
        setCustomerId(undefined);
        setCustomerKeyword("");
        customerSearchKeywordRef.current = "";
        customerSearchRequestRef.current += 1;
        setCustomerSearchError("");
        setCodeError("");
        setCustomerError("");
        if (value.length !== 20 || !/^[A-Za-z0-9]+$/.test(value)) return;
        setLoading(true);
        try {
            const info = await lookupCode(value);
            setCodeInfo(info);
            if (!info) setCodeError("该激活码不存在");
            else if (Number(info.state) !== 1)
                setCodeError(Number(info.state) === 2 ? "该激活码已兑换" : "该激活码已过期");
            else if (isActivationCodeBound(info)) setCustomerId(info.customerInfo?.id);
        } catch {
            setCodeError("激活码查询失败");
        } finally {
            setLoading(false);
        }
    };

    /** 沿用 Vue 未绑定激活码的远程账号搜索，并清除已失效的选择。 */
    const handleCustomerSearch = async (value: string) => {
        setCustomerKeyword(value);
        setCustomerSearchError("");
        setCustomerError("");
        const keyword = value.trim();
        customerSearchKeywordRef.current = keyword;
        // 空关键词用于初始候选和清空后的默认列表，不应丢掉已经选中的账号。
        if (keyword) setCustomerId(undefined);
        const requestId = ++customerSearchRequestRef.current;
        try {
            const options = await searchCustomers(keyword);
            if (requestId === customerSearchRequestRef.current) setCustomerOptions(options);
        } catch {
            if (requestId !== customerSearchRequestRef.current) return;
            setCustomerOptions([]);
            setCustomerSearchError("关联账号搜索失败");
        }
    };

    // 只有未兑换且未绑定的激活码才需要显示关联账号输入，沿用 Vue 弹窗的显隐条件。
    const shouldSelectCustomer = Boolean(codeInfo && Number(codeInfo.state) === 1 && !isActivationCodeBound(codeInfo));

    const submit = async () => {
        if (!/^[A-Za-z0-9]{20}$/.test(code)) {
            setCodeError("只允许输入字母和数字，长度为20");
            return;
        }
        if (!codeInfo) {
            setCodeError("该激活码不存在");
            return;
        }
        if (Number(codeInfo.state) !== 1) {
            setCodeError(Number(codeInfo.state) === 2 ? "该激活码已兑换" : "该激活码已过期");
            return;
        }
        if (!customerId) {
            setCustomerError("请选择关联账号");
            return;
        }
        const selectedCustomer = customerOptions.find(option => option.id === customerId);
        const customerLabel = formatExchangeCustomer(
            selectedCustomer?.customer || codeInfo.customerInfo,
            selectedCustomer?.customer ? undefined : selectedCustomer?.label
        );
        const confirmText = `账号兑换后，${customerLabel}用户将获得${codeInfo.duration || 0}天${formatMemberLevel(
            codeInfo.level
        )}?是否确认兑换`;
        // 旧 Vue 会先关闭兑换表单，再以确认框承接最终提交，避免重复点击造成重复兑换。
        setConfirmData({ code, customerId, text: confirmText });
        onClose();
    };

    const handleConfirm = async () => {
        if (!confirmData) return;
        setConfirmLoading(true);
        try {
            await exchange({ code: confirmData.code, customerId: confirmData.customerId });
            setConfirmData(undefined);
            onUpdated();
        } catch (error) {
            showRequestError(error, "兑换失败");
        } finally {
            setConfirmLoading(false);
        }
    };

    return (
        <>
            <Modal
                open={open}
                title="兑换"
                width={520}
                centered
                styles={exchangeDialogModalStyles}
                onCancel={onClose}
                footer={
                    <Space size={8}>
                        <Button autoInsertSpace={false} {...exchangeDialogButtonProps} onClick={onClose}>
                            取消
                        </Button>
                        <Button
                            autoInsertSpace={false}
                            {...exchangeDialogButtonProps}
                            disabled={banButton}
                            type="primary"
                            onClick={() => void submit()}
                        >
                            提交
                        </Button>
                    </Space>
                }
            >
                <Form colon={false} layout="vertical">
                    <Form.Item
                        label="激活码"
                        required
                        validateStatus={codeError ? "error" : undefined}
                        help={codeError || undefined}
                    >
                        <Input
                            aria-label="激活码"
                            maxLength={20}
                            placeholder="请输入激活码"
                            value={code}
                            onChange={event => void handleCodeChange(event.target.value)}
                        />
                    </Form.Item>
                    {loading ? <span>查询中...</span> : null}
                    {codeInfo ? (
                        <ZhGrid className="mb-5 rounded bg-blue-50 p-5" cols={3} gap={23}>
                            <ZhGridItem>
                                <InfoPair title="等级" value={formatMemberLevel(codeInfo.level)} />
                            </ZhGridItem>
                            <ZhGridItem>
                                <InfoPair title="时长" value={`${codeInfo.duration || 0}天`} />
                            </ZhGridItem>
                            {!shouldSelectCustomer ? (
                                <>
                                    <ZhGridItem>
                                        <InfoPair title="用户ID" value={codeInfo.customerInfo?.code} />
                                    </ZhGridItem>
                                    <ZhGridItem>
                                        <InfoPair
                                            title="个人实名/机构名称"
                                            value={
                                                codeInfo.customerInfo?.institutionName ||
                                                codeInfo.customerInfo?.realName
                                            }
                                        />
                                    </ZhGridItem>
                                    <ZhGridItem>
                                        <InfoPair title="手机号" value={codeInfo.customerInfo?.mobile} />
                                    </ZhGridItem>
                                </>
                            ) : null}
                        </ZhGrid>
                    ) : null}
                    {shouldSelectCustomer ? (
                        <Form.Item
                            label="关联账号"
                            required
                            validateStatus={customerError || customerSearchError ? "error" : undefined}
                            help={customerError || customerSearchError || undefined}
                        >
                            <Select
                                allowClear
                                aria-label="关联账号"
                                placeholder="请输入账号信息搜索"
                                showSearch
                                filterOption={false}
                                value={customerId}
                                searchValue={customerKeyword}
                                options={customerOptions.map(option => ({ value: option.id, label: option.label }))}
                                onSearch={value => void handleCustomerSearch(value)}
                                onChange={value => setCustomerId(value)}
                            />
                        </Form.Item>
                    ) : null}
                </Form>
            </Modal>
            <Modal
                open={Boolean(confirmData)}
                className="member-recharge-confirm-modal"
                title="是否确认兑换"
                width={420}
                centered
                styles={exchangeConfirmModalStyles}
                onCancel={() => {
                    setConfirmData(undefined);
                }}
                okText="确认"
                cancelText="取消"
                cancelButtonProps={exchangeConfirmButtonProps}
                okButtonProps={exchangeConfirmButtonProps}
                confirmLoading={confirmLoading}
                onOk={() => void handleConfirm()}
            >
                {confirmData?.text}
            </Modal>
        </>
    );
}

export function MemberRechargePage({
    loadRecords = loadMemberRechargeRecords,
    lookupCode = lookupMemberActivationCode,
    searchCustomers = searchMemberRechargeCustomers,
    exchange = exchangeMemberActivationCode
}: MemberRechargePageProps) {
    const banButton = useUserStore(state => state.banButton);
    const [records, setRecords] = useState<MemberRechargeRow[]>([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState<MemberRechargeQuery>(defaultQuery);
    const [form, setForm] = useState<Omit<MemberRechargeQuery, "page" | "size" | "sortKey" | "sortOrder">>({});
    const [errorMessage, setErrorMessage] = useState("");
    const [exchangeOpen, setExchangeOpen] = useState(false);

    useEffect(() => {
        let disposed = false;
        void loadRecords(query)
            .then(result => {
                if (disposed) return;
                setRecords(result.list || []);
                setTotal(result.total || 0);
                setErrorMessage("");
            })
            .catch(() => {
                if (disposed) return;
                setRecords([]);
                setTotal(0);
                setErrorMessage("会员充值记录加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadRecords, query]);

    const reload = () => setQuery(current => ({ ...current }));
    const submitFilters = () => setQuery({ page: 1, size: query.size, ...form });
    const resetFilters = () => {
        setForm({});
        setQuery(current => ({ page: 1, size: current.size }));
    };
    // 保留 Vue ProTable 的创建时间排序参数，zhihao-ui 的方向映射为旧接口 sortKey/sortOrder。
    const handleSortChange = useCallback(
        ({ prop, order }: { prop?: string; order: "ascending" | "descending" | null }) => {
            if (prop !== "createdTime") return;
            setQuery(current => {
                const nextQuery = { ...current, page: 1 };
                if (!order) {
                    delete nextQuery.sortKey;
                    delete nextQuery.sortOrder;
                } else {
                    nextQuery.sortKey = "created_time";
                    nextQuery.sortOrder = order === "ascending" ? "asc" : "desc";
                }
                return nextQuery;
            });
        },
        []
    );

    // 用 zhihao-ui-react 列契约承接旧 Vue 会员充值 ProTable 的字段和格式化逻辑。
    const columns = useMemo<TableColumnProps<MemberRechargeRow>[]>(
        () => [
            { key: "id", label: "编号", width: 138, prop: "id" },
            {
                key: "customerInfo",
                label: "账号",
                minWidth: 228,
                prop: "customerInfo",
                render: ({ row }) => formatCustomer(row.customerInfo)
            },
            {
                key: "memberLevel",
                label: "等级",
                width: 130,
                prop: "memberLevel",
                render: ({ row }) => formatMemberLevel(row.memberLevel)
            },
            {
                key: "memberDay",
                label: "时长",
                width: 100,
                prop: "memberDay",
                render: ({ row }) => `${row.memberDay || 0}天`
            },
            {
                key: "openMode",
                label: "支付/兑换方式",
                width: 150,
                prop: "openMode",
                render: ({ row }) => openModeLabels[Number(row.openMode)] || DEFAULT_VALUE
            },
            { key: "activationCode", label: "激活码", minWidth: 148, prop: "activationCode" },
            {
                key: "amount",
                label: "实收金额（元）",
                minWidth: 130,
                prop: "amount",
                align: "right",
                headerAlign: "right",
                render: ({ row }) => formatAmount(row.amount)
            },
            {
                key: "activationDate",
                label: "激活日期",
                width: 110,
                prop: "activationDate",
                render: ({ row }) => formatDateOnly(row.activationDate)
            },
            {
                key: "expireDate",
                label: "到期日期",
                width: 110,
                prop: "expireDate",
                render: ({ row }) => formatDateOnly(row.expireDate)
            },
            {
                key: "createdTime",
                label: "创建时间",
                width: 150,
                prop: "createdTime",
                sortable: true,
                render: ({ row }) => formatDateTime(row.createdTime)
            }
        ],
        []
    );

    return (
        <section className="member-recharge-page flex h-full min-h-full flex-col" aria-label="member-recharge-page">
            <Card className="flex h-full flex-1 flex-col">
                <ZhTable<MemberRechargeRow>
                    rowKey="id"
                    data={records}
                    columns={columns}
                    pagination={false}
                    onSortChange={handleSortChange}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title className="mb-4 mt-0" level={4}>
                                会员充值记录
                            </Typography.Title>

                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}

                            <Space wrap className="admin-table-filter-form mb-4">
                                <Permission anyOf={["ROLE_ORDER_W"]}>
                                    <Button
                                        aria-label="兑换"
                                        disabled={banButton}
                                        type="primary"
                                        onClick={() => setExchangeOpen(true)}
                                    >
                                        兑换
                                    </Button>
                                </Permission>
                                <label>
                                    编号
                                    <Input
                                        aria-label="编号"
                                        maxLength={50}
                                        placeholder="请输入编号搜索"
                                        value={form.id || ""}
                                        onChange={event => setForm({ ...form, id: event.target.value })}
                                    />
                                </label>
                                <label>
                                    账号信息
                                    <Input
                                        aria-label="账号信息"
                                        maxLength={50}
                                        placeholder="请输入账号信息搜索"
                                        value={form.customerKeyWord || ""}
                                        onChange={event => setForm({ ...form, customerKeyWord: event.target.value })}
                                    />
                                </label>
                                <label>
                                    会员等级
                                    <Select
                                        allowClear
                                        aria-label="会员等级"
                                        options={Object.entries(memberLevelLabels).map(([value, label]) => ({
                                            label,
                                            value
                                        }))}
                                        placeholder="请选择会员等级"
                                        value={form.memberLevel}
                                        onChange={value => setForm({ ...form, memberLevel: value })}
                                    />
                                </label>
                                <label>
                                    支付/兑换方式
                                    <Select
                                        allowClear
                                        aria-label="支付/兑换方式"
                                        options={Object.entries(openModeLabels).map(([value, label]) => ({
                                            label,
                                            value
                                        }))}
                                        placeholder="请选择支付方式"
                                        value={form.openMode}
                                        onChange={value => setForm({ ...form, openMode: value })}
                                    />
                                </label>
                                <label>
                                    激活码搜索
                                    <Input
                                        aria-label="激活码搜索"
                                        placeholder="请输入激活码搜索"
                                        value={form.activationCode || ""}
                                        onChange={event => setForm({ ...form, activationCode: event.target.value })}
                                    />
                                </label>
                                <Button
                                    aria-label="查询"
                                    icon={<TableSearchIcon name="search" />}
                                    type="primary"
                                    onClick={submitFilters}
                                >
                                    搜索
                                </Button>
                                <Button
                                    aria-label="重置"
                                    className="table-filter-reset-button"
                                    icon={<TableSearchIcon name="reset" />}
                                    title="重置"
                                    onClick={resetFilters}
                                />
                            </Space>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                onChange={(page, pageSize) =>
                                    setQuery(current => ({
                                        ...current,
                                        page: pageSize !== current.size ? 1 : page,
                                        size: pageSize
                                    }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
            <ExchangeDialog
                open={exchangeOpen}
                lookupCode={lookupCode}
                searchCustomers={searchCustomers}
                exchange={exchange}
                onClose={() => setExchangeOpen(false)}
                onUpdated={reload}
            />
        </section>
    );
}
